"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import {
  AccountBadge,
  Crumbs,
  Page,
  Pill,
  Table,
  Tbody,
  Td,
  Tr,
} from "@/components/ui";
import { HttpError, OpenMatrixOut, api } from "@/lib/api";
import { fmtLots } from "@/lib/fmt";

// Signed lots as a number (Decimal crosses the wire as a string).
const num = (v: string | number | null | undefined): number | null =>
  v === null || v === undefined ? null : Number(v);

// Sort bucket by the first account column — 0 long, 1 short, 2 flat/absent.
// Rows arrive pre-sorted from the backend; we only recompute this to know
// where to drop the two section dividers.
function bucketOf(first: number | null): 0 | 1 | 2 {
  if (first === null || first === 0) return 2;
  return first > 0 ? 0 : 1;
}

function lotClass(n: number | null): string {
  if (n === null || n === 0) return "text-mute/40";
  return n > 0 ? "text-up" : "text-down";
}

function signedLots(n: number | null): string {
  if (n === null || n === 0) return "·";
  return n > 0 ? `+${fmtLots(n)}` : fmtLots(n);
}

function downloadCsv(csv: string, filename: string) {
  const blob = new Blob([csv], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const el = document.createElement("a");
  el.href = url;
  el.download = filename;
  document.body.appendChild(el);
  el.click();
  document.body.removeChild(el);
  URL.revokeObjectURL(url);
}

export default function OpenMatrixPage() {
  const [data, setData] = useState<OpenMatrixOut | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [filter, setFilter] = useState("");

  useEffect(() => {
    setErr(null);
    api.openMatrix()
      .then(setData)
      .catch((e) => {
        if (e instanceof HttpError && e.status === 401) setErr("Please sign in.");
        else setErr(String(e));
      });
  }, []);

  if (err) return <p className="text-sm text-down">{err}</p>;
  if (!data) return <p className="eyebrow text-mute">Loading…</p>;

  const { accounts, rows, snapshot } = data;
  const firstAcct = accounts[0]?.account ?? "—";
  const staleAccts = accounts.filter((a) => a.stale);
  // Client-side underlying filter (case-insensitive substring). Section
  // dividers + row-hot shading recompute over the filtered set below.
  const q = filter.trim().toLowerCase();
  const visibleRows = q
    ? rows.filter((r) => r.underlying.toLowerCase().includes(q))
    : rows;

  // Per-column long/short totals over the visible set — Σ of positive lots and
  // Σ of negative lots down each alias column, plus the Total column. This is
  // the frozen top strip: at-a-glance gross long vs gross short per alias.
  const colLong = accounts.map((_, i) =>
    visibleRows.reduce((s, r) => {
      const v = num(r.lots[i]);
      return s + (v && v > 0 ? v : 0);
    }, 0),
  );
  const colShort = accounts.map((_, i) =>
    visibleRows.reduce((s, r) => {
      const v = num(r.lots[i]);
      return s + (v && v < 0 ? v : 0);
    }, 0),
  );
  const totLong = visibleRows.reduce((s, r) => {
    const v = num(r.total);
    return s + (v && v > 0 ? v : 0);
  }, 0);
  const totShort = visibleRows.reduce((s, r) => {
    const v = num(r.total);
    return s + (v && v < 0 ? v : 0);
  }, 0);

  function exportCsv() {
    const header = ["Underlying", ...accounts.map((a) => a.name), "Total"].join(",");
    const lines = visibleRows.map((r) =>
      [
        r.underlying,
        ...r.lots.map((v) => (v === null || v === undefined ? "" : String(v))),
        r.total ?? "",
      ].join(","),
    );
    downloadCsv([header, ...lines].join("\n"), `open-lots-${snapshot ?? "latest"}.csv`);
  }

  if (accounts.length === 0) {
    return (
      <Page
        eyebrow={<Crumbs items={[{ label: "Positions" }, { label: "Open lots" }]} />}
        title={<>Open <em className="italic">lots</em></>}
      >
        <p className="max-w-prose text-sm text-mute">
          No accounts in scope. Ask an admin to grant access.
        </p>
      </Page>
    );
  }

  // Build body rows with section dividers at first-account bucket changes.
  const bodyRows: React.ReactNode[] = [];
  let prevBucket: number | null = null;
  const colSpanRest = accounts.length + 1; // account cols + Total

  for (const r of visibleRows) {
    const lots = r.lots.map(num);
    const first = lots[0] ?? null;
    const b = bucketOf(first);
    if (b !== prevBucket && b !== 0) {
      const label = b === 1 ? `Short in ${firstAcct}` : `Flat in ${firstAcct}`;
      bodyRows.push(
        <Tr key={`div-${b}`} className="hover:bg-transparent">
          <Td sticky mono={false} className="py-1">
            <span className="eyebrow text-mute/70">{label} ↓</span>
          </Td>
          <td colSpan={colSpanRest} className="border-b border-dashed border-rule" />
        </Tr>,
      );
    }
    prevBucket = b;

    const rowMax = Math.max(1, ...lots.map((v) => Math.abs(v ?? 0)));
    const total = num(r.total);

    bodyRows.push(
      <Tr key={r.underlying}>
        <Td sticky mono={false} className="border-b border-rule/50 font-semibold">
          {r.underlying}
        </Td>
        {lots.map((v, i) => {
          const hot =
            v !== null && v !== 0 && Math.abs(v) >= rowMax * 0.66
              ? v > 0
                ? "bg-up/10"
                : "bg-down/10"
              : "";
          return (
            <Td key={accounts[i].account_id} align="right" className={`border-b border-rule/50 ${lotClass(v)} ${hot}`}>
              {signedLots(v)}
            </Td>
          );
        })}
        <Td align="right" className={`border-b border-rule/50 border-l border-rule ${lotClass(total)} font-semibold`}>
          {signedLots(total)}
        </Td>
      </Tr>,
    );
  }

  return (
    <Page
      eyebrow={<Crumbs items={[{ label: "Positions" }, { label: "Open lots" }]} />}
      title={<>Open <em className="italic">lots</em> — accounts × underlyings</>}
    >
      <p className="max-w-prose text-sm text-mute">
        Signed net delta lots per underlying, one column per alias. IOC fills
        scatter the same position across accounts — this is the single-glance
        view of where the book sits. Latest snapshot per account, whole book.
      </p>

      <div className="flex flex-wrap items-center gap-3">
        <label className="flex items-center gap-2 text-xs">
          <span className="eyebrow text-mute">Underlying:</span>
          <input
            type="text"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="filter…"
            className="w-40 border border-rule bg-transparent px-2 py-1 text-sm uppercase focus:border-foreground focus:outline-none"
          />
          {filter && (
            <button
              type="button"
              onClick={() => setFilter("")}
              className="eyebrow text-mute transition hover:text-foreground"
            >
              clear
            </button>
          )}
        </label>
        <Pill variant="outline">Net delta lots · signed</Pill>
        {snapshot && <Pill variant="outline">Snapshot {snapshot}</Pill>}
        <Pill variant="outline">{accounts.length} accounts</Pill>
        <Pill variant="outline">
          {q ? `${visibleRows.length} / ${rows.length}` : rows.length} underlyings
        </Pill>
        <span className="eyebrow flex items-center gap-3 text-mute">
          <span className="flex items-center gap-1"><span className="inline-block h-2 w-2 bg-up" />long</span>
          <span className="flex items-center gap-1"><span className="inline-block h-2 w-2 bg-down" />short</span>
        </span>
        <button
          type="button"
          onClick={exportCsv}
          className="ml-auto eyebrow text-mute transition hover:text-foreground"
        >
          ↳ Export CSV
        </button>
      </div>

      {staleAccts.length > 0 && (
        <div className="border-l-2 border-down px-3 py-2 text-sm text-down">
          <span className="font-semibold">Stale columns</span> —{" "}
          {staleAccts.map((a) => `${a.account} (${a.as_of ?? "no data"})`).join(", ")}.
          Their lots are the last known book, not today&rsquo;s — likely ingest lag.
        </div>
      )}

      <Table sticky className="max-h-[72vh] overflow-y-auto">
        {/* Single frozen header row (sticky top + sticky first column = Excel
            freeze panes). Each alias cell carries its Σ long / Σ short beneath
            the name, so the per-column totals stay pinned while the ~70
            underlyings scroll. Custom <thead> (not the shared <Thead>) so the
            cells can own their own top/left sticky + z-index without the
            shared component forcing z-10. */}
        <thead>
          <tr>
            <th className="eyebrow py-1.5 pr-4 text-left align-bottom font-normal text-mute sticky left-0 top-0 z-30 bg-background border-b border-rule">
              <div className="flex flex-col gap-1">
                <span>Underlying</span>
                <span className="normal-case tracking-normal text-mute/60">Σ long / short ↓</span>
              </div>
            </th>
            {accounts.map((a, i) => (
              <th
                key={a.account_id}
                className={`eyebrow py-1.5 pr-4 text-right align-bottom font-normal sticky top-0 z-20 bg-background border-b border-rule ${a.stale ? "text-down" : "text-mute"}`}
              >
                <div className="flex flex-col items-end gap-1">
                  <Link href={`/accounts/${a.account_id}`} className="hover:text-foreground" title={a.name}>
                    {a.account}
                  </Link>
                  <AccountBadge is_client_account={a.is_client_account} clientcode={a.clientcode} accountId={a.account_id} />
                  {/* Sigma long / short. Stale-with-nothing shows its marker in the short slot. */}
                  <div className="mt-1 flex w-full flex-col items-end gap-0.5 border-t border-rule/40 pt-1 num text-[11px] normal-case leading-tight tracking-normal">
                    <span className="text-up">{colLong[i] ? `+${fmtLots(colLong[i])}` : "·"}</span>
                    <span className="text-down">{a.stale && colLong[i] === 0 && colShort[i] === 0 ? (a.as_of ?? "no data") : colShort[i] ? `−${fmtLots(Math.abs(colShort[i]))}` : "·"}</span>
                  </div>
                </div>
              </th>
            ))}
            <th className="eyebrow py-1.5 pr-4 text-right align-bottom font-normal text-mute sticky top-0 z-20 bg-background border-b border-l border-rule">
              <div className="flex flex-col items-end gap-1">
                <span>Total</span>
                <div className="mt-1 flex w-full flex-col items-end gap-0.5 border-t border-rule/40 pt-1 num text-[11px] normal-case leading-tight tracking-normal">
                  <span className="text-up">{totLong ? `+${fmtLots(totLong)}` : "·"}</span>
                  <span className="text-down">{totShort ? `−${fmtLots(Math.abs(totShort))}` : "·"}</span>
                </div>
              </div>
            </th>
          </tr>
        </thead>
        <Tbody>{bodyRows}</Tbody>
      </Table>

      <p className="eyebrow text-mute">
        Rows: longs first, then shorts, then flat — ranked by {firstAcct} (first column). · = flat.
      </p>
    </Page>
  );
}
